import React, { useState, useCallback } from "react";
import { setActiveEmailedReportsUser } from "./reportEmailBadges";
import PrebuiltReportsLanding from "./PrebuiltReportsLanding";
import {
  getEligibleStoresForReport,
  getReportById,
  reportVisibleForPermission,
} from "./reportRegistry";
import ReportStorePicker from "./ReportStorePicker";
import { PrebuiltReportStoreNameContext } from "./PrebuiltReportShell";

// Top-level dispatcher for the Pre-built Reports area.
//
// State machine:
//   activeReportId === null  → render the landing tile grid
//   activeReportId === <id>  → render that report's component inside its shell
//
// Each report component receives the standard reporting props (homeStore,
// storeOptions, etc.) plus an onBack callback to return to the landing page.
export default function PrebuiltReportsDisplay(props) {
  const [activeReportId, setActiveReportId] = useState(null);
  // Set during render so it's current before any child's save can finish.
  setActiveEmailedReportsUser(props.userId);
  // Store the reports run against. null = follow the global home store;
  // set once the user explicitly picks a store from the report filter row.
  const [selectedStoreId, setSelectedStoreId] = useState(null);

  const handleOpenReport = useCallback((id) => {
    setActiveReportId(id);
  }, []);

  const handleBack = useCallback(() => {
    setActiveReportId(null);
  }, []);

  const activeReport = activeReportId ? getReportById(activeReportId) : null;
  // Internal-only reports stay hidden from non-internal roles even via a
  // direct/deep-linked id. (The old home-store-level scheduling gate is
  // subsumed by blockedByNoEligibleStore below, which checks per store.)
  const blockedByPermission = !reportVisibleForPermission(
    activeReport,
    props.permissionRole
  );

  const allStores = Array.isArray(props.storeNames) ? props.storeNames : [];
  // Stores the active report may run against: manager+ memberships only,
  // scheduling-enabled for scheduling-backed reports, and holding the
  // report's requiredStoreFlag module. Per-store rather than
  // home-store-level, so an ineligible home store doesn't lock the user out
  // of running the report against another accessible eligible store. Shared
  // with the landing page's tile filter via the registry helper.
  const eligibleStores = getEligibleStoresForReport(
    activeReport,
    allStores,
    props.storeOptions
  );
  // No accessible store is eligible for this report (scheduling off
  // everywhere for a scheduling-backed report, logbook off everywhere for
  // Log Entries, no manager membership anywhere, ...) → don't run it against
  // hidden or forbidden data sources, even via a direct/deep-linked id. This
  // subsumes the old home-store-level scheduling gate. Only meaningful once
  // storeNames has loaded; an empty allStores just means the fetch hasn't
  // resolved yet.
  const blockedByNoEligibleStore =
    allStores.length > 0 && eligibleStores.length === 0;

  const canRenderActive =
    activeReport &&
    activeReport.status === "available" &&
    activeReport.component &&
    !blockedByPermission &&
    !blockedByNoEligibleStore;

  if (canRenderActive) {
    const ReportComponent = activeReport.component;
    // Soft picker gate: stores lacking preferredStoreFlag give a degraded
    // (not hidden/forbidden) report — e.g. Labor Metric Graph loses its
    // schedule-derived labor series on scheduling-off stores — so they're
    // only excluded while a fully-capable store exists. When no store has
    // the flag, every eligible store stays selectable and the report runs
    // degraded, preserving its pre-picker behavior.
    let selectableStores = eligibleStores;
    if (activeReport.preferredStoreFlag) {
      const flagged = eligibleStores.filter(
        (s) => s[activeReport.preferredStoreFlag]
      );
      if (flagged.length > 0) selectableStores = flagged;
    }
    // Every pre-built report is store-scoped off the homeStore prop, so
    // swapping in the picked store re-drives each report's fetches and
    // business-week snapping with no per-report store logic. When there's no
    // (selectable) explicit selection, fall back to the home store only if
    // it qualifies for this report — the home store itself can lack the
    // report's module while another accessible store has it, in which case
    // the first selectable store hosts the report instead.
    const selectionEligible =
      selectedStoreId != null &&
      selectableStores.some((s) => s.id === selectedStoreId);
    const homeStoreEligible = selectableStores.some(
      (s) => s.id === props.homeStore
    );
    const effectiveStore = selectionEligible
      ? selectedStoreId
      : homeStoreEligible
      ? props.homeStore
      : selectableStores[0]?.id ?? props.homeStore;
    // The picked store may belong to a different company than the user's
    // home company; company-scoped fetches (e.g. Log Entries' Type options
    // come from the company's logbook columns) must follow the store.
    const effectiveCompany =
      allStores.find((s) => s.id === effectiveStore)?.company ??
      props.homeCompany;
    const storePicker =
      selectableStores.length > 1 ? (
        <ReportStorePicker
          storeNames={selectableStores}
          value={effectiveStore}
          onChange={setSelectedStoreId}
          labelPosition={
            activeReport.filterLabelStyle === "stacked" ? "top" : "inline"
          }
        />
      ) : undefined;
    const effectiveStoreName = activeReport.crossStore
      ? undefined
      : allStores.find((s) => s.id === effectiveStore)?.store_name;
    return (
      <PrebuiltReportStoreNameContext.Provider value={effectiveStoreName}>
        <ReportComponent
          report={activeReport}
          homeStore={effectiveStore}
          homeCompany={effectiveCompany}
          companyContext={props.homeCompany}
          storeOptions={props.storeOptions}
          storeNames={props.storeNames}
          userId={props.userId}
          onBack={handleBack}
          storePicker={storePicker}
        />
      </PrebuiltReportStoreNameContext.Provider>
    );
  }

  // Either no active report, or the id resolved to something invalid /
  // not-yet-available — fall back to the landing page. We don't try to
  // setState here (that would be a render-time side effect); the bad id
  // simply gets ignored and the user can pick again.
  return (
    <PrebuiltReportsLanding
      onOpenReport={handleOpenReport}
      storeNames={props.storeNames}
      storeOptions={props.storeOptions}
      schedulingEnabled={props.schedulingEnabled}
      permissionRole={props.permissionRole}
      userId={props.userId}
    />
  );
}
